import { useCallback, useEffect, useRef, useState } from 'react';
import { useChatStream } from '../hooks/useChatStream.js';
import { useStreamInfo } from '../hooks/useStreamInfo.js';
import { useChatters } from '../hooks/useChatters.js';
import { useAuth } from '../context/AuthContext.js';
import ChatList from '../components/Chat/ChatList.js';
import EventFeed from '../components/Chat/EventFeed.js';
import ChatInput, { type ReplyTarget } from '../components/Chat/ChatInput.js';
import LoginButton from '../components/auth/LoginButton.js';
import type { ChatMessage } from '../lib/twitch.js';
import type { ChatterRole } from '../hooks/useChatters.js';

function statusDot(status: string): string {
  switch (status) {
    case 'open': return 'bg-green-500';
    case 'connecting': return 'bg-yellow-500 animate-pulse';
    default: return 'bg-red-500';
  }
}

export default function MobileChat() {
  const {
    status,
    messages,
    deletedIds,
    quotaWarning,
    channelEvents,
    redemptionMap,
    chatEventsCutoff,
  } = useChatStream();
  const streamInfo = useStreamInfo();
  const { isLoggedIn } = useAuth();
  const [replyTo, setReplyTo] = useState<ReplyTarget | null>(null);
  const [showViewerList, setShowViewerList] = useState(false);
  const viewerListRef = useRef<HTMLDivElement>(null);
  const { chatters, total: chatterTotal, loading: chattersLoading, error: chattersError } =
    useChatters(showViewerList && streamInfo.live, 30_000);

  useEffect(() => {
    if (!showViewerList) return;
    function handleClick(e: MouseEvent) {
      if (viewerListRef.current && !viewerListRef.current.contains(e.target as Node)) {
        setShowViewerList(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showViewerList]);

  const handleReply = useCallback((msg: ChatMessage) => {
    setReplyTo({
      msgId: msg.externalId,
      displayName: msg.displayName,
      msgBody: msg.text,
    });
  }, []);

  const groups: Array<{ role: ChatterRole; label: string; color: string }> = [
    { role: 'broadcaster', label: 'Broadcaster', color: 'text-yellow-400' },
    { role: 'mod',         label: 'Moderatoren', color: 'text-green-400' },
    { role: 'vip',         label: 'VIPs',        color: 'text-pink-400'  },
    { role: 'viewer',      label: 'Viewer',      color: 'text-slate-400' },
  ];
  const nameColor: Record<ChatterRole, string> = {
    broadcaster: 'text-yellow-300',
    mod:         'text-green-300',
    vip:         'text-pink-300',
    viewer:      'text-slate-300',
  };

  return (
    <div className="h-screen flex flex-col bg-slate-950 text-slate-100 overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 flex-shrink-0 border-b border-slate-800/60 bg-slate-900/40">
        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${statusDot(status)}`} />
        <span className="text-[11px] text-slate-500">abgemiked</span>
        {streamInfo.live && (
          <span className="text-[10px] font-bold text-red-400 uppercase tracking-wide">LIVE</span>
        )}
        {streamInfo.live && streamInfo.viewerCount !== null && (
          <div className="relative ml-auto" ref={viewerListRef}>
            <button
              type="button"
              onClick={() => setShowViewerList((v) => !v)}
              className={`flex items-center gap-0.5 text-xs font-semibold tabular-nums px-1 py-0.5 rounded transition-colors ${
                showViewerList
                  ? 'text-purple-300 bg-purple-900/40'
                  : 'text-slate-300 hover:text-purple-300 hover:bg-purple-900/30'
              }`}
              title="Viewerliste öffnen"
            >
              <svg className="w-3 h-3 text-purple-400 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z"/>
              </svg>
              {streamInfo.viewerCount.toLocaleString('de-DE')}
            </button>
            {showViewerList && (
              <div className="absolute right-0 top-full mt-1 w-56 max-h-80 flex flex-col bg-slate-900 border border-slate-700 rounded shadow-xl z-50">
                <div className="flex items-center justify-between px-2 py-1.5 border-b border-slate-700 flex-shrink-0">
                  <span className="text-[10px] font-bold text-purple-400 uppercase tracking-wide">
                    Viewer im Chat
                  </span>
                  <span className="text-[10px] text-slate-400 tabular-nums">
                    {chattersLoading ? '…' : chatterTotal.toLocaleString('de-DE')}
                  </span>
                </div>
                <div className="overflow-y-auto flex-1">
                  {chattersError ? (
                    <p className="text-[10px] text-red-400 px-2 py-1">{chattersError}</p>
                  ) : chatters.length === 0 && !chattersLoading ? (
                    <p className="text-[10px] text-slate-500 px-2 py-1">Keine Daten</p>
                  ) : (
                    groups.map(({ role, label, color }) => {
                      const members = chatters.filter((c) => c.role === role);
                      if (members.length === 0) return null;
                      return (
                        <div key={role}>
                          <div className={`px-2 pt-1.5 pb-0.5 text-[9px] font-bold uppercase tracking-wider ${color}`}>
                            {label} ({members.length})
                          </div>
                          {members.map((c) => (
                            <div key={c.user_id} className={`px-3 py-0.5 text-[11px] hover:bg-slate-800 truncate ${nameColor[role]}`}>
                              {c.user_name}
                            </div>
                          ))}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <EventFeed events={channelEvents} />
      <ChatList
        messages={messages}
        deletedIds={deletedIds}
        quotaWarning={quotaWarning}
        redemptionMap={redemptionMap}
        channelEvents={channelEvents}
        chatEventsCutoff={chatEventsCutoff}
        onReply={isLoggedIn ? handleReply : undefined}
      />
      {isLoggedIn ? (
        <ChatInput replyTo={replyTo} onClearReply={() => setReplyTo(null)} />
      ) : (
        <div className="border-t border-slate-800 bg-slate-900/60 px-4 py-3 flex items-center gap-3 flex-shrink-0">
          <span className="text-sm text-slate-400">Einloggen um zu schreiben.</span>
          <LoginButton />
        </div>
      )}
    </div>
  );
}
